import { CORES, PALETA, MODELOS, RIPADA_ALTURA_MAX, espessuraChapaPorAltura, espessuraChapaValida } from './calculoAcm';
import type { AcmParams } from './acmGenerator';
import { layoutDeParams, layoutParaParams, sanitizarLayout } from './acmEditor';

// Cor livre (fora da paleta Tecbond): o vendedor detalha nas observações.
export const COR_ESPECIAL = 'Cor Especial';

// Cores antigas do formulário do lead que não existem na paleta Tecbond.
const COR_ALIAS: Record<string, string> = {
    'Grafite': 'Grafite Metálico',
    'Amadeirado': 'Madeira Carvalho',
    'Escovado': 'Cinza Metálico',
};

// Grupos da paleta Tecbond para montar selects (mesma fonte do gerador de pranchas).
export const PALETA_GRUPOS: Array<{ grupo: string; cores: string[] }> = PALETA.map((g: any) => ({
    grupo: g[0] as string,
    cores: Object.keys(g[1]),
}));

export function normalizarCorAcm(cor?: string, fallback = 'Branco'): string {
    if (!cor) return fallback;
    if (CORES[cor] || cor === COR_ESPECIAL) return cor;
    return COR_ALIAS[cor] || fallback;
}

export function isPortaInterna(modeloPorta?: string): boolean {
    const m = (modeloPorta || '').toUpperCase();
    return m.includes('INTERNA') || m.includes('RENOVA');
}

// ---- Tipo de porta: o vendedor escolhe a linha (social/interna) e as opções; o nome do modelo é derivado.

export type LinhaPorta = 'social' | 'interna' | 'ripada' | 'correr' | 'outro' | '';

const LINHAS: LinhaPorta[] = ['social', 'interna', 'ripada', 'correr', 'outro'];

export const NOME_RIPADA = 'PORTA PIVOTANTE RIPADA EM ACM';
export const NOME_CORRER = 'PORTA DE CORRER EM ACM';

/** Linha pelo nome do modelo (leads antigos ou itens sem o campo linha). */
function linhaPeloNome(nome: string): LinhaPorta {
    if (!nome) return '';
    if (nome === 'OUTRO') return 'outro';
    if (nome.includes('RIPAD')) return 'ripada';
    if (nome.includes('CORRER')) return 'correr';
    return isPortaInterna(nome) ? 'interna' : 'social';
}

export interface ModeloPorta {
    linha: LinhaPorta;
    cava: boolean;
    friso: boolean;
    renova: boolean;
}

/** Lê o tipo da porta, inclusive de leads antigos que só têm o nome do modelo. */
export function lerModelo(door: any): ModeloPorta {
    const nome = (door?.modelo_porta || '').toUpperCase();
    const linha: LinhaPorta = LINHAS.includes(door?.linha) ? door.linha : linhaPeloNome(nome);
    return {
        linha,
        // Social: cava por padrão; Interna: só com cava marcada (leads antigos não tinham)
        cava: linha === 'social' ? (typeof door?.cava === 'boolean' ? door.cava : !nome.includes('SOBREPOSTO')) : linha === 'interna' && door?.cava === true,
        friso: (linha === 'social' || linha === 'interna') && (!!door?.com_friso || nome.includes('FRISO')),
        renova: linha === 'interna' && nome.includes('RENOVA'),
    };
}

/** Nome comercial do modelo a partir das escolhas (usado em proposta, venda, etiqueta). */
export function nomeModelo(m: ModeloPorta): string {
    if (m.linha === 'outro') return 'Outro';
    if (m.linha === 'ripada') return NOME_RIPADA;
    if (m.linha === 'correr') return NOME_CORRER;
    if (m.linha === 'interna') return m.renova ? 'KIT PORTA INTERNA RENOVA FÁCIL' : 'KIT PORTA INTERNA NOVO';
    if (m.linha === 'social') return `PORTA SOCIAL PREMIUM + ${m.cava ? 'PUXADOR CAVA' : 'PUXADOR SOBREPOSTO'}${m.friso ? ' + FRISO' : ''}`;
    return '';
}

/** Campos a gravar na porta quando o vendedor muda linha/cava/friso. */
export function camposModelo(m: ModeloPorta) {
    const linha = m.linha;
    const ajustado: ModeloPorta = {
        linha,
        cava: linha === 'social' || linha === 'interna' ? m.cava : false,
        friso: linha === 'social' || linha === 'interna' ? m.friso : false,
        renova: linha === 'interna' ? m.renova : false,
    };
    return { linha, cava: ajustado.cava, com_friso: ajustado.friso, modelo_porta: nomeModelo(ajustado) };
}

// ---- Cores por lado: A = frente, B = verso; cava, friso e marco podem ter cor própria.
// O kit é a cor das ferragens à mostra (maçaneta, dobradiças, puxador sobreposto, concha).

export const CORES_KIT = ['Preto', 'Escovado'];

export interface CoresPorta {
    ladoA: string;
    ladoB: string;
    cavaA: string;
    cavaB: string;
    friso: string;
    kit: string;
    /** Marco/portal/cabeçote e U de borda (ACM). Padrão: mesma cor da porta (lado A). */
    marco: string;
    ladosDiferentes: boolean;
    cavaDiferente: boolean;
    marcoDiferente: boolean;
}

export function lerCores(door: any): CoresPorta {
    const ladoA = door?.cor_a || 'Branco';
    const ladoBGravado = door?.cor_b_campo || door?.cor_b_faixa || '';
    const ladosDiferentes = door?.lados_diferentes ?? (!!ladoBGravado && ladoBGravado !== ladoA);
    const ladoB = ladosDiferentes ? (ladoBGravado || ladoA) : ladoA;
    const cavaDiferente = door?.cava_cor_diferente ?? (!!door?.cor_cava_a && door.cor_cava_a !== ladoA);
    const cavaA = cavaDiferente ? (door?.cor_cava_a || ladoA) : ladoA;
    const cavaB = cavaDiferente ? (ladosDiferentes ? (door?.cor_cava_b || cavaA) : cavaA) : ladoB;
    // Leads antigos gravavam o kit em cor_u (que também pintava o marco); hoje o kit tem campo próprio
    const kit = door?.cor_kit || (door?.marco_cor_diferente === undefined && CORES_KIT.includes(door?.cor_u) ? door.cor_u : 'Preto');
    const marcoDiferente = !!door?.marco_cor_diferente;
    const marco = marcoDiferente ? (door?.cor_marco || ladoA) : ladoA;
    return { ladoA, ladoB, cavaA, cavaB, friso: door?.cor_friso || 'Preto', kit, marco, ladosDiferentes, cavaDiferente, marcoDiferente };
}

/** Campos a gravar na porta a partir das escolhas de cor (mantém o formato lido pelo motor e pela proposta). */
export function camposCores(c: CoresPorta) {
    const ladoB = c.ladosDiferentes ? c.ladoB : c.ladoA;
    const cavaA = c.cavaDiferente ? c.cavaA : c.ladoA;
    const cavaB = c.cavaDiferente ? (c.ladosDiferentes ? c.cavaB : cavaA) : ladoB;
    return {
        cor_a: c.ladoA,
        lados_diferentes: c.ladosDiferentes,
        cor_b_faixa: c.ladosDiferentes ? ladoB : '',
        cor_b_campo: c.ladosDiferentes ? ladoB : '',
        cava_cor_diferente: c.cavaDiferente,
        cor_cava_a: c.cavaDiferente ? cavaA : '',
        cor_cava_b: c.cavaDiferente || c.ladosDiferentes ? cavaB : '',
        cor_friso: c.friso,
        cor_kit: c.kit,
        marco_cor_diferente: c.marcoDiferente,
        cor_marco: c.marcoDiferente ? c.marco : '',
        // Slot "U" do motor das pranchas (marco + U de borda)
        cor_u: c.marcoDiferente ? c.marco : c.ladoA,
    };
}

// Porta que tem prancha/3D (itens "Outro" são livres: painéis, serviços etc.)
export function temPrancha(door: any): boolean {
    return !!door?.modelo_porta && door.modelo_porta !== 'Outro';
}

/** Porta com prancha de fabricação (motor calculoAcm: linhas social, ripada e de correr). */
export function temPranchaFabricacao(door: any): boolean {
    const linha = lerModelo(door).linha;
    return temPrancha(door) && (linha === 'social' || linha === 'ripada' || linha === 'correr');
}

// Aviso quando a altura do vão está fora do limite da linha (mesma regra da checagem da prancha).
export function avisoAlturaVao(door: any): string | null {
    if (!temPrancha(door)) return null;
    const altura = parseInt(door?.vao_altura_mm, 10);
    if (!Number.isFinite(altura) || altura <= 0) return null;
    const linha = lerModelo(door).linha;
    // A ripada é a social com limite de altura próprio
    const m = linha === 'ripada'
        ? { ...MODELOS.social, linha: 'LINHA PIVOTANTE RIPADA', alturaMax: RIPADA_ALTURA_MAX }
        : MODELOS[linha === 'correr' ? 'correr' : isPortaInterna(door.modelo_porta) ? 'interna' : 'social'];
    if (altura < m.alturaMin || altura > m.alturaMax) {
        return `Fora do limite da ${m.linha.toLowerCase()} (${m.alturaMin} a ${m.alturaMax} mm).`;
    }
    return null;
}

/** Escolha manual da espessura da chapa no orçamento ('3' | '4'); qualquer outro valor = automática. */
export function espessuraChapaManual(door: any): 3 | 4 | undefined {
    return espessuraChapaValida(door?.espessura_chapa);
}

/** Espessura da chapa que a porta usa: a escolhida no orçamento ou, em automática, a da altura do vão. */
export function espessuraChapaEfetiva(door: any): 3 | 4 {
    return espessuraChapaManual(door) ?? espessuraChapaPorAltura(parseInt(door?.vao_altura_mm, 10) || 0);
}

// Converte uma porta salva no lead (metadata.acm_doors[i]) nos parâmetros do motor de pranchas.
export function doorToAcmParams(door: any, index: number, leadName?: string): AcmParams {
    const modeloPorta = (door?.modelo_porta || '').toUpperCase();
    const linhaPorta = lerModelo(door).linha;
    // A ripada é a social com chapas ripadas e puxador integrado; a de correr tem modelo próprio
    const ripada = linhaPorta === 'ripada';
    const correr = linhaPorta === 'correr';
    const modelo: 'social' | 'interna' | 'correr' = ripada
        ? 'social'
        : correr ? 'correr'
        : door?.modelo === 'interna' || door?.modelo === 'social'
            ? door.modelo
            : isPortaInterna(modeloPorta) ? 'interna' : 'social';

    const cava = !ripada && !correr && (typeof door?.cava === 'boolean'
        ? door.cava
        : !modeloPorta.includes('SOBREPOSTO'));
    const junta = (typeof door?.junta === 'boolean'
        ? door.junta
        : !!door?.com_friso || modeloPorta.includes('FRISO'));

    const lc = lerCores(door);
    const corA = normalizarCorAcm(lc.ladoA);
    const corB = normalizarCorAcm(lc.ladoB, corA);
    const cores = door?.cores && typeof door.cores === 'object'
        ? door.cores
        : {
            // Todas as bandejas de um lado (inclusive a 3ª, usada com friso) seguem a cor daquele lado
            A1: corA,
            A2: corA,
            A3: corA,
            cavaA: normalizarCorAcm(lc.cavaA, corA),
            B1: corB,
            B2: corB,
            B3: corB,
            cavaB: normalizarCorAcm(lc.cavaB, corB),
            fundo: normalizarCorAcm(door?.cor_friso, corA),
            U: normalizarCorAcm(lc.marco, corA),
        };

    const num = (v: any, def: number) => {
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n > 0 ? n : def;
    };

    const layout: Partial<AcmParams> = layoutParaParams(sanitizarLayout(door?.layout));
    return {
        projeto: (leadName ? `${leadName} - ` : '') + (door?.ambiente || `Porta ${index + 1}`),
        modelo,
        vaoLargura: num(door?.vao_largura_mm, 1200),
        vaoAltura: num(door?.vao_altura_mm, modelo === 'interna' || modelo === 'correr' ? 2100 : 3400),
        fechaduraEixo: num(door?.fechadura, 1000),
        cava,
        // Com lista de frisos no layout, ela manda: há friso se a lista não está vazia
        junta: layout.frisos ? layout.frisos.length > 0 : junta,
        cores,
        qtd: num(door?.quantidade_portas, 1),
        corKit: lc.kit,
        // Detalhes editados no editor de portas (posição/vão da cava, junta, divisões de chapa)
        ...layout,
        ...(ripada ? { linhaRipada: true, alturaMaxima: RIPADA_ALTURA_MAX } : {}),
        ...(door?.alisar ? { alisar: true, alisarMm: num(door?.alisar_mm, 70) } : {}),
        ...(door?.lado_abertura ? { ladoAbertura: door.lado_abertura } : {}),
        // Espessura escolhida no orçamento: vale para a chapa comercial, o plano de corte e a lista de compra
        ...(espessuraChapaManual(door) ? { espessuraChapa: espessuraChapaManual(door) } : {}),
    };
}

/**
 * Campos a gravar na porta do lead depois de editada no editor de portas: medidas, cava/friso (e o nome do modelo
 * derivado deles) e o layout dos detalhes. As cores continuam as do orçamento (o formulário do lead é o dono delas), salvo a cava e os frisos, cuja cor própria vai no layout.
 */
export function camposDaPortaEditada(door: any, p: AcmParams) {
    const atual = lerModelo(door);
    const social = p.modelo === 'social';
    const friso = p.frisos ? p.frisos.length > 0 : !!p.junta;
    const layout = layoutDeParams(p);
    return {
        ...camposModelo({ linha: p.linhaRipada ? 'ripada' : p.modelo, cava: (social || p.modelo === 'interna') && !p.linhaRipada ? !!p.cava : false, friso, renova: p.modelo === 'interna' ? atual.renova : false }),
        vao_largura_mm: String(p.vaoLargura),
        vao_altura_mm: String(p.vaoAltura),
        fechadura: String(p.fechaduraEixo ?? 1000),
        // Portas que já gravam `junta` à parte continuam consistentes com o friso
        ...(typeof door?.junta === 'boolean' ? { junta: friso } : {}),
        // Porta de correr: para onde a folha corre (cabeçote)
        ...(p.modelo === 'correr' && p.ladoAbertura ? { lado_abertura: p.ladoAbertura } : {}),
        layout: Object.keys(layout).length > 0 ? layout : undefined,
        // Espessura trocada no editor volta para o orçamento (vazio = automática)
        espessura_chapa: (espessuraChapaValida(p.espessuraChapa) ? String(p.espessuraChapa) : '') as '' | '3' | '4',
    };
}

// Quantidade de portas de um lead, ignorando itens "Outro" (painéis, serviços, etc.).
export function countAcmDoors(metadata?: Record<string, any> | null): number {
    if (!metadata) return 0;
    if (Array.isArray(metadata.acm_doors)) {
        return metadata.acm_doors
            .filter((d: any) => d?.modelo_porta !== 'Outro')
            .reduce((acc: number, d: any) => acc + (Number(d?.quantidade_portas) || 1), 0);
    }
    if (metadata.quantidade_portas) return Number(metadata.quantidade_portas) || 1;
    return 0;
}

// Valor total do orçamento a partir das portas (usado no WinLeadModal para pré-preencher/recalcular
// o valor da venda quando o lead ACM já tem portas com preço unitário lançado).
export function calculateAcmDoorsTotal(doors?: unknown): number {
    if (!Array.isArray(doors)) return 0;
    let total = 0;
    doors.forEach((d: any) => {
        const qtd = parseInt(d.quantidade_portas) || 1;
        const valUnit = parseFloat(d.valor_unitario) || 0;
        total += (qtd * valUnit);
    });
    return parseFloat(total.toFixed(2));
}

// Soma bruta (sem arredondamento, sem parseInt na quantidade) do valor das portas de um
// orçamento — usada nos formulários que ainda editam o orçamento (LeadModal, tela de proposta),
// diferente de calculateAcmDoorsTotal (usada só no WinLeadModal, já fechado, que arredonda em 2 casas).
export function sumAcmDoorsBudget(doors?: unknown): number {
    if (!Array.isArray(doors)) return 0;
    return doors.reduce((acc: number, door: any) => {
        return acc + ((parseFloat(door.valor_unitario) || 0) * (door.quantidade_portas || 1));
    }, 0);
}

// Cliente ACM que já comprou (venda ligada a este lead) e ainda não abriu um novo orçamento
// depois dela — controla o botão "+ Novo orçamento" no LeadModal. O novo orçamento é outro lead
// (clone) e o lead antigo fica marcado com `novo_orcamento_lead_id`; `ciclo_iniciado_em` só existe
// em leads reabertos pelo fluxo antigo (que reaproveitava o mesmo lead).
export function canReopenAcmBudget(
    lead: { id: number; metadata?: Record<string, any> | null } | undefined,
    latestSale: { lead_id?: number | null; created_at?: string | null; sale_date?: string } | null
): boolean {
    if (!lead || !latestSale) return false;
    if (latestSale.lead_id !== lead.id) return false;
    if (lead.metadata?.novo_orcamento_lead_id) return false;
    const cicloIniciadoEm = lead.metadata?.ciclo_iniciado_em;
    if (!cicloIniciadoEm) return true;
    return new Date(latestSale.created_at || latestSale.sale_date || '') > new Date(cicloIniciadoEm);
}

// Nome do produto da venda a partir das portas do orçamento (ex.: "2x KIT PORTA INTERNA NOVO + 1x Painel ripado").
export function acmProductName(metadata?: Record<string, any> | null): string | null {
    const doors = Array.isArray(metadata?.acm_doors) ? metadata!.acm_doors : [];
    const totais = new Map<string, number>();
    doors.forEach((d: any) => {
        const nome = d?.modelo_porta === 'Outro'
            ? (d?.outro_descricao || '').trim().split('\n')[0].slice(0, 60) || 'Outro'
            : (d?.modelo_porta || '').trim();
        if (!nome) return;
        totais.set(nome, (totais.get(nome) || 0) + (Number(d?.quantidade_portas) || 1));
    });
    // Esquadrias (orçamento fora do configurador de portas): entram pelo total de unidades
    const esquadrias = Array.isArray(metadata?.esquadrias) ? metadata!.esquadrias : [];
    const qtdEsquadrias = esquadrias.reduce((acc: number, e: any) => acc + (Number(e?.quantidade) || 1), 0);
    if (qtdEsquadrias > 0) totais.set('Esquadrias', qtdEsquadrias);
    if (totais.size === 0) return metadata?.tipo_porta || null;
    return Array.from(totais.entries()).map(([nome, qtd]) => `${qtd}x ${nome}`).join(' + ');
}
